import { Divider, Box, FormHelperText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import { InferType } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createAutogiroMandateFile } from 'src/shared/services/autogiroMandateService';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { AutogiroMandateSendStatus } from './autogiroMandateSendStatus';
import { AutogiroMandateStatus } from './autogiroMandateStatus';
import { AutogiroMandateResponse } from '../models/autogiroMandateResponse';
import { useSchemaCreateAutogiroFile } from '../schemas/useSchemaCreateAutogiroFile';

type AutogiroMandateCreateFilesModalProps = {
  mandates?: AutogiroMandateResponse[];
  modalOpen: boolean;
  onClose: () => void;
  onSave: () => void;
};

export const AutogiroMandateCreateFilesModal = (props: AutogiroMandateCreateFilesModalProps) => {
  const { mandates = undefined, modalOpen, onClose, onSave } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const onHandleClose = () => {
    onClose();
  };

  const onHandleSave = () => {
    onSave();
  };

  const createAutogirFileSchema = useSchemaCreateAutogiroFile();
  type CreateAutogiroFileFormInterface = InferType<typeof createAutogirFileSchema>;

  const formMethods = useForm<CreateAutogiroFileFormInterface>({
    resolver: yupResolver(createAutogirFileSchema),
    defaultValues: {
      autogiroMandates: mandates?.map((am) => am.autogiroMandate_id) ?? undefined,
    },
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods;
  const onSubmit = async (data: CreateAutogiroFileFormInterface) => {
    try {
      const res = await createAutogiroMandateFile(data.ownerId, data.autogiroMandates);
      if (isMounted()) {
        toast.success(<>{t('invoice:autogiro.fileCreatedSuccess')}</>);
        ConsoleHelper.log('File RES: ', res);
        onHandleSave();
      }
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: t('invoice:autogiro.tableHeader.firstName'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:autogiro.tableHeader.lastName'),
      sortable: true,
      flex: 1.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName}</>;
      },
    },
    {
      field: 'personalIdentityNumber',
      headerName: t('invoice:autogiro.tableHeader.personalIdentityNumber'),
      sortable: true,
      type: 'number',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.personalIdentityNumber}</>;
      },
    },
    {
      field: 'createdAt',
      headerName: t('invoice:autogiro.tableHeader.createdAt'),
      sortable: true,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdAt)}</>;
      },
    },
    {
      field: 'status',
      headerName: t('invoice:autogiro.tableHeader.status'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <AutogiroMandateStatus item={params.row} />;
      },
    },
    {
      field: 'sent',
      headerName: t('invoice:autogiro.tableHeader.sent'),
      sortable: true,
      type: 'number',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <AutogiroMandateSendStatus item={params.row} />;
      },
    },
    {
      field: 'autogiroMandate_id',
      headerName: t('fundraiser:tableHeader.dateCreated'),
      sortable: true,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.personalIdentityNumber}</>;
      },
    },
  ];

  return (
    <>
      <GenericDialog
        open={modalOpen}
        onClose={onHandleClose}
        onSave={handleSubmit(onSubmit)}
        dialogTitle={t('invoice:autogiro.tabCreateFile')}
        padding={0}
        dialogContent={
          <Box>
            <Divider />
            {mandates && (
              <Box sx={{ p: 2 }}>
                <FormProvider {...formMethods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Box mb={2}>
                      <InvoiceGroups
                        callback={(ownId) => {
                          setValue('ownerId', ownId);
                        }}
                        placeholder={t('event:chooseOwnerId')}
                        emptyLabel={t('event:chooseOwnerId')}
                        autoSelect
                        required
                        name='ownerId'
                        ownerId={getValues('ownerId')}
                      />
                      {errors.ownerId && (
                        <FormHelperText error sx={{ ml: 1 }}>
                          <em>{errors.ownerId.message}</em>
                        </FormHelperText>
                      )}
                    </Box>
                    <DataGrid
                      sx={{
                        minHeight: 250,
                        '& .MuiDataGrid-row': { minHeight: '2rem !important' },
                        '& .lastColumnSeparator .MuiDataGrid-columnSeparator': {
                          display: 'none',
                        },
                        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                          outline: 'none', // Ta bort blå outline på celler när man klickar på dem
                        },
                      }}
                      columnVisibilityModel={{
                        autogiroMandate_id: false,
                      }}
                      density='compact'
                      onRowClick={undefined}
                      getRowId={(row) => row.autogiroMandate_id}
                      columns={columns}
                      rows={mandates}
                    />
                  </form>
                </FormProvider>
              </Box>
            )}
          </Box>
        }
      />
    </>
  );
};
